/**
 * LIVE EMAIL API TEST SUITE
 * 
 * Comprehensive testing of:
 * - Graph API email operations (send, search, move, etc.)
 * - Database queries and operations
 * - JSON response schemas
 * - AI agent orchestration and workflows
 * - Authentication token validation
 * 
 * All tests use real stored authentication tokens from database
 */

import { createClient } from '@blinkdotnew/sdk';
import { emailActions } from '../emailActions';
import { gpt5NanoAgent, executeUserIntent } from '../gpt5NanoAgent';
import { unifiedAIAgentService, initializeAIAgentService } from '../unifiedAIAgentService';

const blink = createClient({
  projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87',
  authRequired: false
});

/**
 * Test Report Structure
 */
interface TestResult {
  testName: string;
  category: string;
  status: 'PASS' | 'FAIL' | 'SKIP';
  timestamp: string;
  duration: number;
  request?: any;
  response?: any;
  error?: string;
  details: string;
}

class LiveEmailApiTestSuite {
  private testResults: TestResult[] = [];
  private userId: string = '';
  private accessTokens: Map<string, string> = new Map();

  async runAllTests(): Promise<TestResult[]> {
    console.log('🚀 Starting LIVE Email API Test Suite...\n');

    try {
      // Get current user
      const user = await blink.auth.me();
      this.userId = user.id;
      console.log(`✅ Authenticated user: ${user.email} (${this.userId})\n`);

      // Initialize AI services
      await initializeAIAgentService(this.userId);
      emailActions.setUserId(this.userId);
      console.log('✅ AI services initialized\n');

      // Retrieve all email accounts and access tokens
      await this.retrieveEmailAccounts();

      // Run test categories
      await this.testAuthenticationTokens();
      await this.testEmailSearchOperations();
      await this.testEmailSendingWorkflows();
      await this.testEmailManagementActions();
      await this.testDatabaseOperations();
      await this.testJsonSchemas();
      await this.testAIAgentOrchestration();
      await this.testCalendarOperations();
      await this.testAgenticWorkflows();

      console.log('\n✅ All tests completed!\n');
    } catch (error) {
      console.error('❌ Test suite error:', error);
      this.addResult({
        testName: 'Suite Initialization',
        category: 'Setup',
        status: 'FAIL',
        error: error instanceof Error ? error.message : 'Unknown error',
        details: 'Failed to initialize test suite'
      });
    }

    return this.testResults;
  }

  /**
   * TEST 1: RETRIEVE AND VALIDATE AUTHENTICATION TOKENS
   */
  private async retrieveEmailAccounts(): Promise<void> {
    console.log('📋 Retrieving email accounts and access tokens...\n');

    try {
      const accounts = await blink.db.emailAccounts.list({
        where: { userId: this.userId },
        limit: 10
      });

      console.log(`Found ${accounts.length} connected email accounts:\n`);

      for (const account of accounts) {
        const provider = (account as any).provider;
        const email = (account as any).emailAddress || (account as any).email_address;
        const hasToken = !!(account.accessToken || (account as any).access_token);
        
        console.log(`  📧 ${provider.toUpperCase()}: ${email}`);
        console.log(`     Token Available: ${hasToken ? '✅' : '❌'}`);
        console.log(`     Account ID: ${account.id}\n`);

        if (hasToken) {
          this.accessTokens.set(provider, account.accessToken || (account as any).access_token);
        }
      }
    } catch (error) {
      console.error('Error retrieving accounts:', error);
    }
  }

  /**
   * TEST 1: AUTHENTICATION TOKENS
   */
  private async testAuthenticationTokens(): Promise<void> {
    const startTime = Date.now();
    console.log('🔐 TEST 1: AUTHENTICATION TOKENS\n');

    try {
      // Test 1.1: Retrieve Outlook token
      if (this.accessTokens.has('outlook')) {
        const token = this.accessTokens.get('outlook')!;
        const isValid = token && token.length > 20 && token.startsWith('EwA');

        this.addResult({
          testName: '1.1 - Outlook Access Token Valid',
          category: 'Authentication',
          status: isValid ? 'PASS' : 'FAIL',
          duration: Date.now() - startTime,
          details: isValid 
            ? `✅ Valid Outlook token retrieved (${token.substring(0, 20)}...)`
            : '❌ Invalid Outlook token format'
        });

        // Test 1.2: Validate token with Graph API
        try {
          const response = await fetch('https://graph.microsoft.com/v1.0/me', {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          const isAuthenticated = response.status === 200;
          this.addResult({
            testName: '1.2 - Outlook Token Graph API Validation',
            category: 'Authentication',
            status: isAuthenticated ? 'PASS' : 'FAIL',
            response: { status: response.status },
            duration: Date.now() - startTime,
            details: isAuthenticated 
              ? '✅ Token authenticated with Microsoft Graph API'
              : `❌ Graph API returned ${response.status}`
          });
        } catch (error) {
          this.addResult({
            testName: '1.2 - Outlook Token Graph API Validation',
            category: 'Authentication',
            status: 'FAIL',
            error: error instanceof Error ? error.message : 'Unknown error',
            duration: Date.now() - startTime,
            details: '❌ Failed to validate with Graph API'
          });
        }
      }

      // Test 1.3: Retrieve Gmail token
      if (this.accessTokens.has('gmail')) {
        const token = this.accessTokens.get('gmail')!;
        const isValid = token && token.length > 20;

        this.addResult({
          testName: '1.3 - Gmail Access Token Valid',
          category: 'Authentication',
          status: isValid ? 'PASS' : 'FAIL',
          duration: Date.now() - startTime,
          details: isValid 
            ? `✅ Valid Gmail token retrieved (${token.substring(0, 20)}...)`
            : '❌ Invalid Gmail token'
        });
      }
    } catch (error) {
      this.addResult({
        testName: 'Authentication Tests',
        category: 'Authentication',
        status: 'FAIL',
        error: error instanceof Error ? error.message : 'Unknown error',
        duration: Date.now() - startTime,
        details: '❌ Authentication test suite failed'
      });
    }
  }

  /**
   * TEST 2: EMAIL SEARCH OPERATIONS
   */
  private async testEmailSearchOperations(): Promise<void> {
    const startTime = Date.now();
    console.log('\n🔍 TEST 2: EMAIL SEARCH OPERATIONS\n');

    try {
      // Test 2.1: Simple search
      const result1 = await emailActions.search({
        query: 'test',
        limit: 10
      });

      this.addResult({
        testName: '2.1 - Simple Email Search',
        category: 'Email Search',
        status: 'PASS',
        response: { resultsCount: result1.length },
        duration: Date.now() - startTime,
        details: `✅ Found ${result1.length} emails matching 'test'`
      });

      // Test 2.2: Search with filters
      const result2 = await emailActions.search({
        query: 'urgent',
        hasAttachment: true,
        isStarred: false
      });

      this.addResult({
        testName: '2.2 - Search with Filters (Attachment)',
        category: 'Email Search',
        status: 'PASS',
        response: { resultsCount: result2.length },
        duration: Date.now() - startTime,
        details: `✅ Found ${result2.length} emails with attachments`
      });

      // Test 2.3: Search with date range
      const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      const result3 = await emailActions.search({
        query: '*',
        afterDate: oneWeekAgo
      });

      this.addResult({
        testName: '2.3 - Search with Date Range',
        category: 'Email Search',
        status: 'PASS',
        response: { resultsCount: result3.length },
        duration: Date.now() - startTime,
        details: `✅ Found ${result3.length} emails from last 7 days`
      });

      // Test 2.4: Live Graph API search
      if (this.accessTokens.has('outlook')) {
        try {
          const response = await fetch('https://ben3ou87--outlook-search.functions.blink.new', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              accessToken: this.accessTokens.get('outlook'),
              query: 'invoice',
              maxResults: 20
            })
          });

          const liveResults = await response.json();
          this.addResult({
            testName: '2.4 - Live Outlook Graph API Search',
            category: 'Email Search',
            status: response.ok ? 'PASS' : 'FAIL',
            response: { status: response.status, resultsCount: liveResults.results?.length || 0 },
            duration: Date.now() - startTime,
            details: response.ok 
              ? `✅ Live search found ${liveResults.results?.length || 0} results`
              : `❌ API returned ${response.status}`
          });
        } catch (error) {
          this.addResult({
            testName: '2.4 - Live Outlook Graph API Search',
            category: 'Email Search',
            status: 'FAIL',
            error: error instanceof Error ? error.message : 'Unknown error',
            duration: Date.now() - startTime,
            details: '❌ Live Graph API search failed'
          });
        }
      }
    } catch (error) {
      this.addResult({
        testName: 'Email Search Operations',
        category: 'Email Search',
        status: 'FAIL',
        error: error instanceof Error ? error.message : 'Unknown error',
        duration: Date.now() - startTime,
        details: '❌ Search operations test failed'
      });
    }
  }

  /**
   * TEST 3: EMAIL SENDING WORKFLOWS
   */
  private async testEmailSendingWorkflows(): Promise<void> {
    const startTime = Date.now();
    console.log('\n📧 TEST 3: EMAIL SENDING WORKFLOWS\n');

    try {
      // Get user account
      const accounts = await blink.db.emailAccounts.list({
        where: { userId: this.userId },
        limit: 1
      });

      if (accounts.length === 0) {
        this.addResult({
          testName: '3.1 - Send Email (Live API)',
          category: 'Email Send',
          status: 'SKIP',
          duration: Date.now() - startTime,
          details: '⏭️ No email accounts connected'
        });
        return;
      }

      // Test 3.1: Create draft
      const draftResult = await emailActions.createDraft({
        to: [(accounts[0] as any).emailAddress || (accounts[0] as any).email_address],
        subject: '[TEST] Draft Email ' + new Date().toISOString(),
        body: 'This is a test draft email created at ' + new Date().toISOString()
      });

      this.addResult({
        testName: '3.1 - Create Email Draft',
        category: 'Email Send',
        status: 'PASS',
        response: { draftId: draftResult.id },
        duration: Date.now() - startTime,
        details: `✅ Draft created: ${draftResult.id}`
      });

      // Test 3.2: Send email via Graph API
      try {
        const provider = (accounts[0] as any).provider;
        const endpoint = provider === 'gmail' 
          ? 'https://ben3ou87--gmail-send.functions.blink.new'
          : 'https://ben3ou87--outlook-send.functions.blink.new';

        const sendResponse = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            accessToken: provider === 'gmail' 
              ? this.accessTokens.get('gmail')
              : this.accessTokens.get('outlook'),
            to: ['test@example.com'],
            subject: '[TEST] Live API Email ' + new Date().getTime(),
            body: 'Test email sent via live Graph API at ' + new Date().toISOString()
          })
        });

        const sendResult = await sendResponse.json();
        this.addResult({
          testName: '3.2 - Send Email (Live Graph API)',
          category: 'Email Send',
          status: sendResponse.ok ? 'PASS' : 'FAIL',
          response: { status: sendResponse.status, messageId: sendResult.messageId },
          duration: Date.now() - startTime,
          details: sendResponse.ok
            ? `✅ Email sent via ${provider.toUpperCase()}: ${sendResult.messageId}`
            : `❌ Send failed: ${sendResult.error}`
        });
      } catch (error) {
        this.addResult({
          testName: '3.2 - Send Email (Live Graph API)',
          category: 'Email Send',
          status: 'FAIL',
          error: error instanceof Error ? error.message : 'Unknown error',
          duration: Date.now() - startTime,
          details: '❌ Failed to send via Graph API'
        });
      }

      // Test 3.3: Update draft
      const updateResult = await emailActions.updateDraft(draftResult.id, {
        subject: '[TEST UPDATED] ' + draftResult.subject,
        body: 'Updated body at ' + new Date().toISOString()
      });

      this.addResult({
        testName: '3.3 - Update Email Draft',
        category: 'Email Send',
        status: 'PASS',
        response: { draftId: updateResult.id },
        duration: Date.now() - startTime,
        details: '✅ Draft updated successfully'
      });
    } catch (error) {
      this.addResult({
        testName: 'Email Sending Workflows',
        category: 'Email Send',
        status: 'FAIL',
        error: error instanceof Error ? error.message : 'Unknown error',
        duration: Date.now() - startTime,
        details: '❌ Sending workflow test failed'
      });
    }
  }

  /**
   * TEST 4: EMAIL MANAGEMENT ACTIONS
   */
  private async testEmailManagementActions(): Promise<void> {
    const startTime = Date.now();
    console.log('\n📁 TEST 4: EMAIL MANAGEMENT ACTIONS\n');

    try {
      // Get a test email
      const emails = await blink.db.messages_enhanced.list({
        where: { userId: this.userId },
        limit: 1
      });

      if (emails.length === 0) {
        this.addResult({
          testName: '4.x - Email Management',
          category: 'Email Management',
          status: 'SKIP',
          duration: Date.now() - startTime,
          details: '⏭️ No emails in database'
        });
        return;
      }

      const emailId = emails[0].id;

      // Test 4.1: Mark read
      await emailActions.markRead(emailId, true);
      this.addResult({
        testName: '4.1 - Mark Email as Read',
        category: 'Email Management',
        status: 'PASS',
        duration: Date.now() - startTime,
        details: '✅ Email marked as read'
      });

      // Test 4.2: Star email
      await emailActions.star(emailId, true);
      this.addResult({
        testName: '4.2 - Star Email',
        category: 'Email Management',
        status: 'PASS',
        duration: Date.now() - startTime,
        details: '✅ Email starred'
      });

      // Test 4.3: Mark important
      await emailActions.markImportant(emailId, true);
      this.addResult({
        testName: '4.3 - Mark as Important',
        category: 'Email Management',
        status: 'PASS',
        duration: Date.now() - startTime,
        details: '✅ Email marked as important'
      });

      // Test 4.4: Add label
      await emailActions.addLabel(emailId, 'test-label');
      this.addResult({
        testName: '4.4 - Add Label',
        category: 'Email Management',
        status: 'PASS',
        duration: Date.now() - startTime,
        details: '✅ Label added to email'
      });

      // Test 4.5: Archive email
      await emailActions.archive(emailId);
      this.addResult({
        testName: '4.5 - Archive Email',
        category: 'Email Management',
        status: 'PASS',
        duration: Date.now() - startTime,
        details: '✅ Email archived'
      });
    } catch (error) {
      this.addResult({
        testName: 'Email Management Actions',
        category: 'Email Management',
        status: 'FAIL',
        error: error instanceof Error ? error.message : 'Unknown error',
        duration: Date.now() - startTime,
        details: '❌ Management actions test failed'
      });
    }
  }

  /**
   * TEST 5: DATABASE OPERATIONS
   */
  private async testDatabaseOperations(): Promise<void> {
    const startTime = Date.now();
    console.log('\n🗄️ TEST 5: DATABASE OPERATIONS\n');

    try {
      // Test 5.1: Query messages_enhanced table
      const messages = await blink.db.messages_enhanced.list({
        where: { userId: this.userId },
        limit: 5,
        orderBy: { receivedDate: 'desc' }
      });

      this.addResult({
        testName: '5.1 - Query messages_enhanced Table',
        category: 'Database',
        status: 'PASS',
        response: { rowCount: messages.length },
        duration: Date.now() - startTime,
        details: `✅ Retrieved ${messages.length} messages from database`
      });

      // Test 5.2: Query emailAccounts table
      const accounts = await blink.db.emailAccounts.list({
        where: { userId: this.userId }
      });

      this.addResult({
        testName: '5.2 - Query emailAccounts Table',
        category: 'Database',
        status: 'PASS',
        response: { rowCount: accounts.length },
        duration: Date.now() - startTime,
        details: `✅ Retrieved ${accounts.length} email accounts`
      });

      // Test 5.3: Database create operation
      const testRecord = await blink.db.messages_enhanced.create({
        id: `test_${Date.now()}`,
        userId: this.userId,
        source: 'local',
        subject: `[TEST] Record at ${new Date().toISOString()}`,
        bodyText: 'Test database record',
        bodyHtml: '<p>Test database record</p>'
      });

      this.addResult({
        testName: '5.3 - Create Database Record',
        category: 'Database',
        status: 'PASS',
        response: { recordId: testRecord.id },
        duration: Date.now() - startTime,
        details: `✅ Created test record: ${testRecord.id}`
      });

      // Test 5.4: Database update operation
      await blink.db.messages_enhanced.update(testRecord.id, {
        subject: `[UPDATED] ${testRecord.subject}`
      });

      this.addResult({
        testName: '5.4 - Update Database Record',
        category: 'Database',
        status: 'PASS',
        duration: Date.now() - startTime,
        details: '✅ Record updated successfully'
      });
    } catch (error) {
      this.addResult({
        testName: 'Database Operations',
        category: 'Database',
        status: 'FAIL',
        error: error instanceof Error ? error.message : 'Unknown error',
        duration: Date.now() - startTime,
        details: '❌ Database operations test failed'
      });
    }
  }

  /**
   * TEST 6: JSON SCHEMA VALIDATION
   */
  private async testJsonSchemas(): Promise<void> {
    const startTime = Date.now();
    console.log('\n📋 TEST 6: JSON SCHEMA VALIDATION\n');

    try {
      // Test 6.1: Email response schema
      const emails = await blink.db.messages_enhanced.list({
        limit: 1
      });

      if (emails.length > 0) {
        const email = emails[0];
        const requiredFields = [
          'id', 'userId', 'subject', 'bodyText', 
          'senderEmail', 'receivedDate'
        ];

        const hasAllFields = requiredFields.every(field => field in email);
        this.addResult({
          testName: '6.1 - Email Response Schema',
          category: 'JSON Schemas',
          status: hasAllFields ? 'PASS' : 'FAIL',
          response: { email },
          duration: Date.now() - startTime,
          details: hasAllFields ? '✅ Email schema valid' : '❌ Missing required fields'
        });
      }

      // Test 6.2: Agent response schema
      const agentResponse = {
        thinking: 'Processing user request',
        action: {
          type: 'email_send',
          parameters: { to: ['test@example.com'], subject: 'Test' },
          confidence: 85
        },
        reasoning: 'User wants to send email',
        validation: {
          isValid: true,
          errors: [],
          warnings: []
        }
      };

      const agentSchemaValid = 
        agentResponse.thinking &&
        agentResponse.action.type &&
        agentResponse.action.parameters &&
        typeof agentResponse.action.confidence === 'number' &&
        agentResponse.validation.isValid !== undefined;

      this.addResult({
        testName: '6.2 - Agent Response Schema',
        category: 'JSON Schemas',
        status: agentSchemaValid ? 'PASS' : 'FAIL',
        response: { agentResponse },
        duration: Date.now() - startTime,
        details: agentSchemaValid ? '✅ Agent schema valid' : '❌ Invalid agent response'
      });
    } catch (error) {
      this.addResult({
        testName: 'JSON Schema Validation',
        category: 'JSON Schemas',
        status: 'FAIL',
        error: error instanceof Error ? error.message : 'Unknown error',
        duration: Date.now() - startTime,
        details: '❌ Schema validation test failed'
      });
    }
  }

  /**
   * TEST 7: AI AGENT ORCHESTRATION
   */
  private async testAIAgentOrchestration(): Promise<void> {
    const startTime = Date.now();
    console.log('\n🤖 TEST 7: AI AGENT ORCHESTRATION\n');

    try {
      // Test 7.1: Process user intent
      const result = await gpt5NanoAgent.processUserIntent(
        'Search for emails from my boss received in the last week'
      );

      this.addResult({
        testName: '7.1 - Process User Intent',
        category: 'AI Agent',
        status: result.validation.isValid ? 'PASS' : 'FAIL',
        response: { action: result.action.type, confidence: result.action.confidence },
        duration: Date.now() - startTime,
        details: result.validation.isValid
          ? `✅ Intent processed: ${result.action.type} (${result.action.confidence}% confidence)`
          : `❌ Validation failed: ${result.validation.errors.join(', ')}`
      });

      // Test 7.2: Execute user intent
      const execution = await executeUserIntent(
        'Create a draft email to john@example.com about the project status'
      );

      this.addResult({
        testName: '7.2 - Execute User Intent',
        category: 'AI Agent',
        status: execution.success ? 'PASS' : 'FAIL',
        response: { success: execution.success },
        duration: Date.now() - startTime,
        details: execution.success
          ? `✅ Intent executed: ${execution.agent_response?.action.type}`
          : `❌ Execution failed: ${execution.error}`
      });

      // Test 7.3: Unified AI Agent Service
      const intentResult = await unifiedAIAgentService.executeUserIntent(
        'Find all unread emails'
      );

      this.addResult({
        testName: '7.3 - Unified AI Agent Service',
        category: 'AI Agent',
        status: intentResult.success ? 'PASS' : 'FAIL',
        response: { action: intentResult.action, confidence: intentResult.confidence },
        duration: Date.now() - startTime,
        details: intentResult.success
          ? `✅ Service executed: ${intentResult.action}`
          : `❌ Service failed: ${intentResult.error}`
      });
    } catch (error) {
      this.addResult({
        testName: 'AI Agent Orchestration',
        category: 'AI Agent',
        status: 'FAIL',
        error: error instanceof Error ? error.message : 'Unknown error',
        duration: Date.now() - startTime,
        details: '❌ Agent orchestration test failed'
      });
    }
  }

  /**
   * TEST 8: CALENDAR OPERATIONS
   */
  private async testCalendarOperations(): Promise<void> {
    const startTime = Date.now();
    console.log('\n📅 TEST 8: CALENDAR OPERATIONS\n');

    try {
      const { unifiedCalendarService } = await import('../calendar/unifiedCalendarService');

      // Test 8.1: Search events
      const now = new Date();
      const future = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

      const events = await unifiedCalendarService.getEventsByDateRange(now, future);

      this.addResult({
        testName: '8.1 - Search Calendar Events',
        category: 'Calendar',
        status: 'PASS',
        response: { eventCount: events.length },
        duration: Date.now() - startTime,
        details: `✅ Found ${events.length} calendar events`
      });

      // Test 8.2: Create event
      const newEvent = await unifiedCalendarService.createEvent({
        title: '[TEST] Meeting at ' + new Date().getTime(),
        start: new Date(now.getTime() + 1 * 24 * 60 * 60 * 1000),
        end: new Date(now.getTime() + 1 * 24 * 60 * 60 * 1000 + 60 * 60 * 1000)
      });

      this.addResult({
        testName: '8.2 - Create Calendar Event',
        category: 'Calendar',
        status: 'PASS',
        response: { eventId: newEvent.id },
        duration: Date.now() - startTime,
        details: `✅ Event created: ${newEvent.id}`
      });
    } catch (error) {
      this.addResult({
        testName: 'Calendar Operations',
        category: 'Calendar',
        status: 'FAIL',
        error: error instanceof Error ? error.message : 'Unknown error',
        duration: Date.now() - startTime,
        details: '❌ Calendar operations test failed'
      });
    }
  }

  /**
   * TEST 9: AGENTIC WORKFLOWS
   */
  private async testAgenticWorkflows(): Promise<void> {
    const startTime = Date.now();
    console.log('\n⚙️ TEST 9: AGENTIC WORKFLOWS\n');

    try {
      // Test 9.1: Multi-step workflow - Search and Reply
      const searchResult = await unifiedAIAgentService.searchEmails({
        query: 'test',
        maxResults: 5
      });

      if (searchResult.length > 0) {
        const emailId = (searchResult[0] as any).id;
        
        await unifiedAIAgentService.replyToEmail({
          messageId: emailId,
          body: 'Thanks for your email! [TEST AUTO-REPLY]'
        });

        this.addResult({
          testName: '9.1 - Workflow: Search and Reply',
          category: 'Agentic Workflows',
          status: 'PASS',
          duration: Date.now() - startTime,
          details: `✅ Searched ${searchResult.length} emails and replied to first`
        });
      } else {
        this.addResult({
          testName: '9.1 - Workflow: Search and Reply',
          category: 'Agentic Workflows',
          status: 'SKIP',
          duration: Date.now() - startTime,
          details: '⏭️ No emails found for workflow test'
        });
      }

      // Test 9.2: Automation - Archive unread after review
      const unreadEmails = await blink.db.messages_enhanced.list({
        where: { 
          userId: this.userId,
          isRead: '0'
        },
        limit: 3
      });

      for (const email of unreadEmails) {
        await emailActions.markRead(email.id, true);
      }

      this.addResult({
        testName: '9.2 - Workflow: Automation (Mark Read)',
        category: 'Agentic Workflows',
        status: 'PASS',
        response: { processed: unreadEmails.length },
        duration: Date.now() - startTime,
        details: `✅ Automated: Marked ${unreadEmails.length} emails as read`
      });

      // Test 9.3: Complex workflow - Inbox Zero
      const allEmails = await unifiedAIAgentService.searchEmails({
        query: '*'
      });

      const archived = allEmails.filter(e => (e as any).labels?.includes('archived')).length;

      this.addResult({
        testName: '9.3 - Workflow: Inbox Management',
        category: 'Agentic Workflows',
        status: 'PASS',
        response: { totalEmails: allEmails.length, archived },
        duration: Date.now() - startTime,
        details: `✅ Total ${allEmails.length} emails, ${archived} archived`
      });
    } catch (error) {
      this.addResult({
        testName: 'Agentic Workflows',
        category: 'Agentic Workflows',
        status: 'FAIL',
        error: error instanceof Error ? error.message : 'Unknown error',
        duration: Date.now() - startTime,
        details: '❌ Workflow test failed'
      });
    }
  }

  /**
   * Add test result to report
   */
  private addResult(result: Omit<TestResult, 'timestamp'>): void {
    this.testResults.push({
      ...result,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Generate HTML test report
   */
  generateHtmlReport(): string {
    const passed = this.testResults.filter(r => r.status === 'PASS').length;
    const failed = this.testResults.filter(r => r.status === 'FAIL').length;
    const skipped = this.testResults.filter(r => r.status === 'SKIP').length;

    let html = `
<!DOCTYPE html>
<html>
<head>
  <title>Live Email API Test Report</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 20px; background: #f5f5f5; }
    .header { background: #2c3e50; color: white; padding: 20px; border-radius: 5px; }
    .summary { display: flex; gap: 20px; margin: 20px 0; }
    .stat { background: white; padding: 15px; border-radius: 5px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
    .stat.pass { border-left: 4px solid #27ae60; }
    .stat.fail { border-left: 4px solid #e74c3c; }
    .stat.skip { border-left: 4px solid #f39c12; }
    .test { background: white; padding: 15px; margin: 10px 0; border-radius: 5px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
    .test.pass { border-left: 4px solid #27ae60; }
    .test.fail { border-left: 4px solid #e74c3c; }
    .test.skip { border-left: 4px solid #f39c12; }
    .test-name { font-weight: bold; }
    .test-category { color: #7f8c8d; font-size: 0.9em; }
    .test-details { color: #555; margin-top: 10px; }
    .test-response { background: #ecf0f1; padding: 10px; margin-top: 10px; border-radius: 3px; font-family: monospace; font-size: 0.85em; max-height: 200px; overflow-y: auto; }
    .test-error { color: #e74c3c; }
  </style>
</head>
<body>
  <div class="header">
    <h1>🚀 Live Email API Test Report</h1>
    <p>Generated: ${new Date().toLocaleString()}</p>
  </div>
  
  <div class="summary">
    <div class="stat pass">
      <strong>Passed</strong><br/>
      ${passed}
    </div>
    <div class="stat fail">
      <strong>Failed</strong><br/>
      ${failed}
    </div>
    <div class="stat skip">
      <strong>Skipped</strong><br/>
      ${skipped}
    </div>
  </div>

  <h2>Test Results</h2>
`;

    // Group by category
    const byCategory = new Map<string, TestResult[]>();
    for (const result of this.testResults) {
      if (!byCategory.has(result.category)) {
        byCategory.set(result.category, []);
      }
      byCategory.get(result.category)!.push(result);
    }

    for (const [category, results] of byCategory) {
      html += `<h3>${category}</h3>`;
      for (const result of results) {
        html += `
  <div class="test ${result.status.toLowerCase()}">
    <div class="test-name">${result.testName}</div>
    <div class="test-category">${result.category} • ${result.duration}ms</div>
    <div class="test-details">${result.details}</div>
    ${result.response ? `<div class="test-response">Response: ${JSON.stringify(result.response, null, 2)}</div>` : ''}
    ${result.error ? `<div class="test-response test-error">Error: ${result.error}</div>` : ''}
  </div>
`;
      }
    }

    html += `
</body>
</html>
`;
    return html;
  }
}

// Export test suite
export const liveEmailApiTestSuite = new LiveEmailApiTestSuite();
